// src/components/widgets/EditUserForm.js
import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api'; 
import './style/EditUserForm.css';

const EditUserForm = ({ user, onUserUpdated, onCancel, currentUserRole }) => {
  const [formData, setFormData] = useState({
    firstName: '', 
    lastName: '', 
    email: '', 
    jobTitle: '', 
    phone: '', 
    organisation: '', 
    role: '', 
    status: 'Active', 
    password: ''
  });

  const [orgOptions, setOrgOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [orgsData, rolesData] = await Promise.all([
          apiService.getOrganisations(),
          apiService.getGroups()
        ]);
        setOrgOptions(orgsData);
        setRoleOptions(rolesData);
      } catch (err) {
        setError("Failed to load form data.");
      }
    };
    fetchDropdownData();
  }, []);

  useEffect(() => {
    if (user) {
      let first = '';
      let last = '';
      if (user.username) {
        const nameParts = user.username.split(' ');
        first = nameParts[0] || '';
        last = nameParts.slice(1).join(' ') || '';
      }
      setFormData({
        firstName: first,
        lastName: last,
        email: user.email || '',
        jobTitle: user.job_title || '', 
        phone: user.phone || '', 
        organisation: user.organisationId || '', 
        role: user.role || '',
        status: user.status || 'Active',
        password: '' 
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    const submissionData = {
      username: `${formData.firstName} ${formData.lastName}`.trim(),
      email: formData.email,
      jobTitle: formData.jobTitle, 
      phone: formData.phone,
      organisationId: parseInt(formData.organisation),
      role: formData.role,
      status: formData.status,
      password: formData.password
    };

    if (!submissionData.password) delete submissionData.password;

    try {
      await apiService.updateUser(user.id, submissionData);
      onUserUpdated(); 
    } catch (err) {
      console.error("Update Error:", err);
      if (err.status === 409) {
        setError('This email address is already in use.');
      } else {
        setError(err.message || 'Failed to update user');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Filter Roles based on Organisation Type
  const getFilteredRoles = () => {
    const selectedOrgId = parseInt(formData.organisation);
    const selectedOrg = orgOptions.find(org => org.id === selectedOrgId);
    
    if (!selectedOrg) return roleOptions;

    const orgType = selectedOrg.organisationType || selectedOrg.type || '';
    const isHeadOffice = orgType.trim().toLowerCase() === 'head office account';

    return roleOptions.filter(role => {
      if (role.roleKey === 'admin' && !isHeadOffice) {
        return false;
      }
      return true;
    });
  };

  const isRoleDisabled = currentUserRole === 'system_admin';

  // --- NEW HELPER: Check if Contact Only ---
  const isContactOnly = formData.role === 'contact_only';

  return (
    <form className="euf-container" onSubmit={handleSubmit}>
      <h2>Edit User</h2>
      {error && <div className="euf-error-message">{error}</div>}

      <div className="euf-group">
        <label className="euf-required">First Name</label>
        <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required />
      </div>
      <div className="euf-group">
        <label className="euf-required">Last Name</label>
        <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required />
      </div>
      
      <div className="euf-group euf-full-width">
        <label>User Email <span className="euf-optional">(Cannot be changed)</span></label>
        <input type="email" name="email" value={formData.email} disabled className="euf-disabled-input"/>
      </div>

      <div className="euf-group">
        <label>Job Title</label>
        <input type="text" name="jobTitle" value={formData.jobTitle} onChange={handleChange} placeholder="e.g. Sales Manager" />
      </div>

      <div className="euf-group">
        <label>Phone <span className="euf-optional">(Optional)</span></label>
        <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="e.g. +1 555 000 0000" />
      </div>

      <div className="euf-group">
        <label className="euf-required">Organisation</label>
        <select name="organisation" value={formData.organisation} onChange={handleChange} required>
          <option value="">Select Organisation</option>
          {orgOptions.map(org => (
            <option key={org.id} value={org.id}>{org.name}</option>
          ))}
        </select>
      </div>

      <div className="euf-group">
        <label className="euf-required">Role</label>
        <select 
          name="role" 
          value={formData.role} 
          onChange={handleChange} 
          required 
          disabled={isRoleDisabled}
          className={isRoleDisabled ? "euf-disabled-input" : ""}
        >
          <option value="">Select Role</option>
          {getFilteredRoles().map(role => (
            <option key={role.id} value={role.roleKey}>{role.name}</option>
          ))}
        </select>
        {isRoleDisabled && <span className="euf-optional" style={{color: 'orange'}}>System Admins cannot change roles.</span>}
      </div>

      <div className="euf-group">
        <label>Status</label>
        <select name="status" value={formData.status} onChange={handleChange}>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
          <option value="Pending">Pending</option>
        </select>
      </div>

      {/* --- CHANGE: Hide Password if Contact Only --- */}
      {!isContactOnly && (
        <div className="euf-group euf-full-width">
          <label>Password <span className="euf-optional">(Leave blank to keep current)</span></label>
          <div className="euf-password-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="New password (optional)"
            />
            <button type="button" className="euf-password-toggle" onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
            </button>
          </div>
        </div>
      )}

      <div className="euf-actions">
        <button type="button" className="euf-btn-cancel" onClick={onCancel}>Cancel</button>
        <button type="submit" className="euf-btn-save" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
};

export default EditUserForm;
